import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { api } from '../api';
import { buildDashboardUrl } from '../state';

interface Event {
    id: string;
    name: string;
    ip: string;
    language: string;
    user_agent: string;
    headers: any;
    data: any;
    client_info: any;
    session_id: string;
    page: string;
    referrer: string;
    date_created: string;
}

interface Session {
    id: string;
    property_id: string;
    ip: string;
    country_code: string;
    browser: string;
    screen_size: string;
    operating_system: string;
    date_created: string;
    referrer?: string;
    duration?: number;
}

const SessionDetail = () => {
    const location = useLocation();
    const [session, setSession] = useState<Session | null>(null);
    const [events, setEvents] = useState<Event[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedEvent, setExpandedEvent] = useState<string | null>(null);

    // Extract session ID from URL
    const sessionId = location.url.split('/').pop();

    useEffect(() => {
        if (sessionId) {
            setLoading(true);
            setError(null);
            
            api(`/api/events/${sessionId}`)
                .then(result => {
                    if (result.success) {
                        setSession(result.session);
                        setEvents(result.events);
                    } else {
                        setError(result.error || 'Failed to fetch events');
                    }
                    setLoading(false);
                })
                .catch(err => {
                    console.error('Error fetching events:', err);
                    setError('Failed to fetch events');
                    setLoading(false);
                });
        }
    }, [sessionId]);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleString('it-IT', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const formatDuration = (seconds: number | undefined) => {
        if (!seconds) return 'N/A';
        
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        
        const parts = [];
        if (hours > 0) parts.push(`${hours}h`);
        if (minutes > 0) parts.push(`${minutes}m`);
        if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);
        
        return parts.join(' ');
    };

    const toggleEventDetails = (eventId: string) => {
        setExpandedEvent(expandedEvent === eventId ? null : eventId);
    };

    const handleBackToSessions = (clickEvent: MouseEvent) => {
        if (window.history.length > 1) {
            clickEvent.preventDefault();
            window.history.back();
        }
    };

    const formatJSON = (obj: any) => {
        if (!obj) return 'N/A';
        try {
            return JSON.stringify(obj, null, 2);
        } catch {
            return String(obj);
        }
    };

    if (loading) {
        return (
            <div className="container" style={{ padding: '0 2rem' }}>
                <div className="loading">Caricamento in corso...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container" style={{ padding: '0 2rem' }}>
                <div className="error">{error}</div>
            </div>
        );
    }

    return (
        <div className="container" style={{ padding: '0 2rem', maxWidth: '1280px', margin: '0 auto' }}>
            <div style={{ marginBottom: '24px', marginTop: '20px' }}>
                <a
                    href={buildDashboardUrl('/dashboard/sessions', { preserveCurrentSearch: true })}
                    className="back-link"
                    onClick={handleBackToSessions}
                >
                    ← Torna alle Sessioni
                </a>
            </div>
            
            <h2 style={{ marginBottom: '24px' }}>Dettagli della Sessione</h2>
            
            {/* Session Information Card */}
            {session && (
                <div className="session-info-card" style={{
                    background: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '24px',
                    marginBottom: '32px'
                }}>
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                        gap: '20px' 
                    }}>
                        <div>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px', fontWeight: '600', textTransform: 'uppercase' }}>
                                ID Sessione
                            </div>
                            <div style={{ fontFamily: 'monospace', fontSize: '13px', wordBreak: 'break-all' }}>
                                {session.id}
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px', fontWeight: '600', textTransform: 'uppercase' }}>
                                Iniziato
                            </div>
                            <div style={{ fontSize: '14px' }}>
                                {formatDate(session.date_created)}
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px', fontWeight: '600', textTransform: 'uppercase' }}>
                                Durata
                            </div>
                            <div style={{ fontSize: '14px' }}>
                                {formatDuration(session.duration)}
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px', fontWeight: '600', textTransform: 'uppercase' }}>
                                Indirizzo IP
                            </div>
                            <div style={{ fontSize: '14px' }}>
                                {session.ip ? session.ip.replace(/\.\d+$/, '.xxx') : 'N/A'}
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px', fontWeight: '600', textTransform: 'uppercase' }}>
                                Paese
                            </div>
                            <div style={{ fontSize: '14px' }}>
                                {session.country_code || 'N/A'}
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px', fontWeight: '600', textTransform: 'uppercase' }}>
                                Browser
                            </div>
                            <div style={{ fontSize: '14px' }}>
                                {session.browser || 'N/A'}
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px', fontWeight: '600', textTransform: 'uppercase' }}>
                                Sistema Operativo
                            </div>
                            <div style={{ fontSize: '14px' }}>
                                {session.operating_system || 'N/A'}
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px', fontWeight: '600', textTransform: 'uppercase' }}>
                                Dimensione Schermo
                            </div>
                            <div style={{ fontSize: '14px' }}>
                                {session.screen_size || 'N/A'}
                            </div>
                        </div>
                        {session.referrer && (
                            <div>
                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px', fontWeight: '600', textTransform: 'uppercase' }}>
                                    Referrer
                                </div>
                                <div style={{ fontSize: '14px', wordBreak: 'break-all' }}>
                                    {session.referrer}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <h3 style={{ marginBottom: '16px', fontSize: '20px' }}>Eventi ({events.length})</h3>

            {events.length === 0 ? (
                <div className="no-data">Nessun evento trovato per questa sessione.</div>
            ) : (
                <div className="events-list">
                    {events.map((event, index) => (
                        <div key={event.id} className="event-card">
                            <div className="event-header" onClick={() => toggleEventDetails(event.id)}>
                                <div className="event-title">
                                    <span className="event-number">{index + 1}.</span>
                                    <span className="event-name">{event.name || 'Unnamed Event'}</span>
                                    <span className="event-page">{event.page}</span>
                                </div>
                                <div className="event-meta">
                                    <span className="event-time">{formatDate(event.date_created)}</span>
                                    <span className="expand-icon">
                                        {expandedEvent === event.id ? '▼' : '▶'}
                                    </span>
                                </div>
                            </div>

                            {expandedEvent === event.id && (
                                <div className="event-details">
                                    <div className="event-detail-grid">
                                        <div className="event-detail-row">
                                            <span className="detail-label">Indirizzo IP:</span>
                                            <span className="detail-value">{event.ip ? event.ip.replace(/\.\d+$/, '.xxx') : 'N/A'}</span>
                                        </div>
                                        <div className="event-detail-row">
                                            <span className="detail-label">Lingua:</span>
                                            <span className="detail-value">{event.language || 'N/A'}</span>
                                        </div>
                                        <div className="event-detail-row">
                                            <span className="detail-label">Referrer:</span>
                                            <span className="detail-value">{event.referrer || 'N/A'}</span>
                                        </div>
                                        <div className="event-detail-row">
                                            <span className="detail-label">User Agent:</span>
                                            <span className="detail-value detail-value-full">
                                                {event.user_agent || 'N/A'}
                                            </span>
                                        </div>
                                    </div>

                                    {event.data && Object.keys(event.data).length > 0 && (
                                        <div className="event-detail-section">
                                            <h4>Dati Evento</h4>
                                            <pre className="json-display">{formatJSON(event.data)}</pre>
                                        </div>
                                    )}

                                    {event.client_info && Object.keys(event.client_info).length > 0 && (
                                        <div className="event-detail-section">
                                            <h4>Client Info</h4>
                                            <pre className="json-display">{formatJSON(event.client_info)}</pre>
                                        </div>
                                    )}

                                    {event.headers && Object.keys(event.headers).length > 0 && (
                                        <div className="event-detail-section">
                                            <h4>Headers</h4>
                                            <pre className="json-display">{formatJSON(event.headers)}</pre>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <style>{`
                .back-link {
                    color: #5850ec;
                    text-decoration: none;
                    font-size: 14px;
                    display: inline-flex;
                    align-items: center;
                    font-weight: 500;
                    transition: color 0.2s;
                }
                .back-link:hover {
                    color: #4338ca;
                    text-decoration: underline;
                }
                .session-info-card {
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                }
                .events-list {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                    margin-bottom: 40px;
                }
                .event-card {
                    background: white;
                    border: 1px solid #e0e0e0;
                    border-radius: 8px;
                    overflow: hidden;
                    transition: all 0.2s;
                }
                .event-card:hover {
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                    border-color: #d0d0d0;
                }
                .event-header {
                    padding: 18px 20px;
                    cursor: pointer;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    background: #fafafa;
                    transition: background 0.2s;
                }
                .event-header:hover {
                    background: #f0f0f0;
                }
                .event-title {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex: 1;
                    flex-wrap: wrap;
                }
                .event-number {
                    font-weight: 600;
                    color: #666;
                    min-width: 35px;
                }
                .event-name {
                    font-weight: 600;
                    color: #1f2937;
                    font-size: 15px;
                }
                .event-page {
                    color: #5850ec;
                    font-size: 13px;
                    font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
                    background: #f0f0ff;
                    padding: 4px 8px;
                    border-radius: 4px;
                }
                .event-meta {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }
                .event-time {
                    color: #6b7280;
                    font-size: 13px;
                    font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
                }
                .expand-icon {
                    color: #9ca3af;
                    font-size: 12px;
                    width: 20px;
                    text-align: center;
                    transition: color 0.2s;
                }
                .event-header:hover .expand-icon {
                    color: #6b7280;
                }
                .event-details {
                    padding: 24px;
                    background: white;
                    border-top: 1px solid #e5e7eb;
                }
                .event-detail-grid {
                    display: flex;
                    flex-direction: column;
                    gap: 14px;
                    margin-bottom: 20px;
                }
                .event-detail-row {
                    display: flex;
                    gap: 16px;
                    align-items: flex-start;
                }
                .detail-label {
                    font-weight: 600;
                    color: #6b7280;
                    min-width: 120px;
                    flex-shrink: 0;
                    font-size: 13px;
                }
                .detail-value {
                    color: #1f2937;
                    flex: 1;
                    word-break: break-word;
                    font-size: 14px;
                }
                .detail-value-full {
                    font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
                    font-size: 12px;
                    line-height: 1.6;
                }
                .event-detail-section {
                    margin-top: 20px;
                    padding-top: 20px;
                    border-top: 1px solid #e5e7eb;
                }
                .event-detail-section h4 {
                    margin: 0 0 12px 0;
                    color: #374151;
                    font-size: 14px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }
                .json-display {
                    background: #f9fafb;
                    padding: 16px;
                    border-radius: 6px;
                    font-size: 12px;
                    overflow-x: auto;
                    margin: 0;
                    border: 1px solid #e5e7eb;
                    font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
                    line-height: 1.6;
                    color: #1f2937;
                }
                .no-data {
                    padding: 60px 20px;
                    text-align: center;
                    color: #9ca3af;
                    background: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    font-size: 15px;
                }
                .loading, .error {
                    padding: 60px 20px;
                    text-align: center;
                    font-size: 16px;
                }
                .error {
                    color: #dc2626;
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                    border-radius: 8px;
                }
                code {
                    background: #f3f4f6;
                    padding: 3px 8px;
                    border-radius: 4px;
                    font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
                    font-size: 13px;
                    color: #1f2937;
                }
                
                @media (max-width: 768px) {
                    .event-title {
                        flex-direction: column;
                        align-items: flex-start;
                    }
                    .event-meta {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 8px;
                    }
                    .detail-label {
                        min-width: 100px;
                    }
                }
            `}</style>
        </div>
    );
};

export default SessionDetail;
